import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import css from './accordion.module.scss';

export type AccordionItem = {
  /** Stable key used for open/close tracking. */
  key: string;
  /** Header content — string or custom node. */
  header: ReactNode;
  /** Body content, rendered only while open. */
  children: ReactNode;
  /** Disable the header (e.g. "coming soon" checks). */
  disabled?: boolean;
};

type AccordionProps = {
  items: AccordionItem[];
  /** Keys currently open. Multiple may be open at once. */
  openKeys: string[];
  onToggle: (key: string) => void;
  className?: string;
};

/**
 * Multi-open accordion. Unlike `TabBar`, more than one item can be expanded at
 * a time — the caller owns the `openKeys` set and toggles it.
 */
export const Accordion = ({
  items,
  openKeys,
  onToggle,
  className,
}: AccordionProps) => (
  <div className={joinCss(css.root, className)}>
    {items.map((item) => {
      const isOpen = openKeys.includes(item.key);
      return (
        <div
          key={item.key}
          className={joinCss(css.item, isOpen && css.itemIsOpen)}
        >
          <button
            type="button"
            className={joinCss(
              css.header,
              item.disabled && css.headerIsDisabled
            )}
            aria-expanded={isOpen}
            disabled={item.disabled}
            onClick={() => onToggle(item.key)}
          >
            <span className={css.headerLabel}>{item.header}</span>
            <ChevronDown
              size={18}
              className={joinCss(css.chevron, isOpen && css.chevronIsOpen)}
            />
          </button>
          {isOpen && !item.disabled && (
            <div className={css.body}>{item.children}</div>
          )}
        </div>
      );
    })}
  </div>
);
